import { Button, Text, useIsDesignLanguage, DesignLanguage, useModal, Icon, Divider, Heading, useIsBreakpointLargeAndUp } from '@afterpaytouch/core'
import Image from 'next/image'
import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { Route } from '../../../config/router'
import { BASE_PATH } from '../../../env'
import {
  selectPurchaseMethod,
  setCheckoutStatus,
  setSelectedPaymentType,
  useAppDispatch,
  useAppropriatePaymentMethod,
  useAppRouter,
  useCachedFeatureFlag,
  useHasSeenPaymentTypesPBIRejected,
  useShowIdvModal,
} from '../../../state'
import { useCheckoutSizes } from '../../../utils/responsive'
import styles from './style.module.css'
import { Terms } from './Terms'
import { PaymentType } from '@afterpay/types'
import { CheckoutStatus, Features, IdentityVerificationModalKey } from '../../../model'
import { usePBIRejectedRedesignedAlertBannerHeading, usePBIRejectedText } from './helper'
import { TrackingEvent } from '../../../model/monitoring'
import { IdentityVerificationModal } from '../../IdentityVerificationModal'
import { useMonitoringWithEnduringEventProperties } from '../../../integrations/monitoring'
import { useTranslation } from '../../../utils/convergence'

export const PaymentTypesPBIRejected: FunctionComponent = () => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const isDesktop = useIsBreakpointLargeAndUp()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { showModal } = useModal(IdentityVerificationModalKey)
  const [lock, setLock] = useState<boolean>(false)
  const { extraSmallTextSize, textSize, buttonSize, sectionHeadingSize } = useCheckoutSizes()
  const paymentMethod = useAppropriatePaymentMethod()
  const { seenPaymentTypesPBIRejected, saveToTags } = useHasSeenPaymentTypesPBIRejected()
  const hasSeenPaymentTypesPBIRejected = useRef<boolean>(seenPaymentTypesPBIRejected) // prevents immediate re-render when tag is set
  const showIDVModal = useShowIdvModal()
  const isRedesignedPBIRejectedPageEnabled = useCachedFeatureFlag(Features.REDESIGNED_PBI_REJECTED_PAGE, false)
  const redesignedAlertBannerHeading = usePBIRejectedRedesignedAlertBannerHeading()
  const redesignedSubheading = t('consumerLending:paymentTypesPBIRejected.alertBanner.genericSubheading')

  const illustrationSrc = [BASE_PATH, 'images/payment-types-pbi-rejected-illustration.svg'].join('/')
  const redesignedIllustrationSrc = [BASE_PATH, 'images/cash-app-afterpay-calendar.png'].join('/')

  const heading = !hasSeenPaymentTypesPBIRejected.current ? t('consumerLending:paymentTypesB:firstViewHeading') : t('consumerLending:paymentTypesB:heading')
  const subheading = usePBIRejectedText()
  const aprDisclaimer = t('consumerLending:paymentTypesB:aprDisclaimer')
  const byTheWay = t('consumerLending:paymentTypesB:byTheWay')
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)

  const handleContinueClick = (): void => {
    setLock(true)

    dispatch(setSelectedPaymentType(PaymentType.PCL))

    // the selected paymentType doesn't get picked up so passing it explicitly
    logEvent(TrackingEvent.PRESSED_CL_ENTRY_PAGE_CONTINUE_BUTTON, {
      paymentType: PaymentType.PCL,
    })

    const hasPaymentMethod = paymentMethod !== null
    if (showIDVModal) {
      showModal()
    } else if (hasPaymentMethod) {
      dispatch(selectPurchaseMethod(paymentMethod))
      router.push(Route.PAYMENT_OPTIONS)
    } else {
      dispatch(setCheckoutStatus(CheckoutStatus.LackOfEligiblePaymentMethod))
      router.push(Route.PAYMENT_OPTIONS)
    }
  }

  useEffect(() => {
    if (!seenPaymentTypesPBIRejected) {
      saveToTags()
    }
  }, [seenPaymentTypesPBIRejected])

  useEffect(() => {
    router.prefetch(Route.PAYMENT_OPTIONS)
  }, [router])

  useEffect(() => {
    logEvent(TrackingEvent.VIEWED_CL_ENTRY_PAGE, {
      isRedesignedPBIRejectedPageEnabled,
    })
  }, [])

  // TODO: Once flag is 100% rollout, use this component instead of the old one
  if (isRedesignedPBIRejectedPageEnabled) {
    return (
      <>
        <div className={styles.alertBanner}>
          <div>
            <Icon name='AlertFill' size='Large' />
          </div>
          <div>
            <Text bold size={textSize}>
              {redesignedAlertBannerHeading}
            </Text>
            <Text size={textSize}>{redesignedSubheading}</Text>
          </div>
        </div>
        <Divider kind='Hairline' />
        <div className={styles.imageContainer}>
          <Image src={redesignedIllustrationSrc} alt={t('consumerLending:paymentTypesPBIRejected.illustrationAlt')} width={288} height={365} />
        </div>
        <div className={styles.contentSection}>
          <Text size='XXL'>{t('consumerLending:paymentTypesPBIRejected.title')}</Text>
          <Text size={textSize}>{t('consumerLending:paymentTypesPBIRejected.subheading1')}</Text>
          <Text size={textSize}>{t('consumerLending:paymentTypesPBIRejected.subheading2')}</Text>
        </div>
        <Divider kind='Hairline' margin='Large' />
        <Text size={'XXS'} color='Neutral 500' fontFace='CashSansMono'>
          {t('consumerLending:paymentTypesPBIRejected.aprDisclaimer')}
        </Text>
        <Terms />
        <Button
          kind={isArcadeDesignLanguage ? 'Primary' : 'Secondary'}
          loading={lock}
          disabled={lock}
          type='button'
          padding='Fluid'
          size={buttonSize}
          onClick={handleContinueClick}
          testNameSpace='continue'
        >
          {t('consumerLending:paymentTypesB.continue')}
        </Button>
        <IdentityVerificationModal setClicked={setLock} />
      </>
    )
  }

  return (
    <>
      <div className={isDesktop ?? false ? styles.PBIRejectedWrapperDesktop : styles.PBIRejectedWrapperMobile}>
        <Heading size='XXL'>{heading}</Heading>
        <div className={styles.PBIRejectedSubheader}>
          <Text size='S'>{subheading}</Text>
        </div>
        <div className={styles.paymentTypesAPRDisclaimer}></div>
        <Text size={extraSmallTextSize} color='Gray50'>
          {aprDisclaimer}
        </Text>
        <div className={styles.PBIRejectedIllustration}>
          <Image
            data-testid='payment-types-pbi-rejected-scene'
            src={illustrationSrc}
            alt='Payment Types PBI Rejected Scene'
            width='206'
            height='220'
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
          />
        </div>
        <div className={styles.PBIRejectedByTheWay}>
          <Heading size={sectionHeadingSize}>{byTheWay}</Heading>
        </div>
        <Terms />
        <div className={styles.PBIRejectedAgreeButton}>
          <Button
            kind={isArcadeDesignLanguage ? 'Primary' : 'Secondary'}
            loading={lock}
            disabled={lock}
            type='button'
            padding='Fluid'
            size={buttonSize}
            onClick={handleContinueClick}
            testNameSpace='continue'
          >
            {t('consumerLending:paymentTypesB:continue')}
          </Button>
        </div>
      </div>
      <IdentityVerificationModal setClicked={setLock} />
    </>
  )
}
